import React, { createContext, useContext, useReducer, useEffect,type ReactNode } from 'react'
import { APIWeb } from '@/api'

// 币种选择相关类型定义
interface CurrencyItem {
  icon: string
  name: string
  description: string
  symbol: string
  chainList: any[]
}

interface NetworkItem {
  chainId?: string
  chainName?: string
  symbol?: string
}

interface CurrencySelection {
  selectedCurrency: CurrencyItem | null
  currentNetwork: NetworkItem | null
}

// 状态类型定义
interface AppState {
  userInfo: MemberInfoResponse | null
  // 虚拟卡信息
  electronicCardInfo: CardInfoResponse | null
  hasElectronicCard: boolean | null
  // 实体卡信息（预留）
  physicalCardInfo: PhysicalCardInfoResponse | null
  hasPhysicalCard: boolean | null
  // 商家配置
  merchantConfig: GetBusinessConfigResponse | null
  currencySelection: CurrencySelection
  loading: {
    userInfo: boolean
    electronicCardInfo: boolean
    physicalCardInfo: boolean
    merchantConfig: boolean
  }
  error: {
    userInfo: string | null
    electronicCardInfo: string | null
    physicalCardInfo: string | null
    merchantConfig: string | null
  }
}

// Action 类型定义
type AppAction =
  | { type: 'SET_USER_INFO_LOADING'; payload: boolean }
  | { type: 'SET_USER_INFO_SUCCESS'; payload: MemberInfoResponse }
  | { type: 'SET_USER_INFO_ERROR'; payload: string }
  // 虚拟卡 Actions
  | { type: 'SET_ELECTRONIC_CARD_INFO_LOADING'; payload: boolean }
  | { type: 'SET_ELECTRONIC_CARD_INFO_SUCCESS'; payload: { cardInfo: CardInfoResponse | null; hasCard: boolean } }
  | { type: 'SET_ELECTRONIC_CARD_INFO_ERROR'; payload: string }
  // 实体卡 Actions
  | { type: 'SET_PHYSICAL_CARD_INFO_LOADING'; payload: boolean }
  | { type: 'SET_PHYSICAL_CARD_INFO_SUCCESS'; payload: { cardInfo: PhysicalCardInfoResponse | null; hasCard: boolean } }
  | { type: 'SET_PHYSICAL_CARD_INFO_ERROR'; payload: string }
  // 商家配置 Actions
  | { type: 'SET_MERCHANT_CONFIG_LOADING'; payload: boolean }
  | { type: 'SET_MERCHANT_CONFIG_SUCCESS'; payload: GetBusinessConfigResponse }
  | { type: 'SET_MERCHANT_CONFIG_ERROR'; payload: string }
  | { type: 'SET_SELECTED_CURRENCY'; payload: CurrencyItem }
  | { type: 'SET_CURRENT_NETWORK'; payload: NetworkItem }
  | { type: 'CLEAR_CURRENCY_SELECTION' }
  | { type: 'CLEAR_USER_INFO' }
  | { type: 'CLEAR_ELECTRONIC_CARD_INFO' }
  | { type: 'CLEAR_PHYSICAL_CARD_INFO' }
  | { type: 'CLEAR_MERCHANT_CONFIG' }
  | { type: 'CLEAR_ALL' }
  | { type: 'HYDRATE_FROM_STORAGE'; payload: Partial<AppState> }

// 初始状态
const initialState: AppState = {
  userInfo: null,
  electronicCardInfo: null,
  hasElectronicCard: null,
  physicalCardInfo: null,
  hasPhysicalCard: null,
  merchantConfig: null,
  currencySelection: {
    selectedCurrency: null,
    currentNetwork: null
  },
  loading: {
    userInfo: false,
    electronicCardInfo: false,
    physicalCardInfo: false,
    merchantConfig: false
  },
  error: {
    userInfo: null,
    electronicCardInfo: null,
    physicalCardInfo: null,
    merchantConfig: null
  }
}

// Reducer
const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'SET_USER_INFO_LOADING':
      return {
        ...state,
        loading: { ...state.loading, userInfo: action.payload },
        error: { ...state.error, userInfo: null }
      }
    
    case 'SET_USER_INFO_SUCCESS':
      return {
        ...state,
        userInfo: action.payload,
        loading: { ...state.loading, userInfo: false },
        error: { ...state.error, userInfo: null }
      }
    
    case 'SET_USER_INFO_ERROR':
      return {
        ...state,
        loading: { ...state.loading, userInfo: false },
        error: { ...state.error, userInfo: action.payload }
      }
    
    // 虚拟卡 Cases
    case 'SET_ELECTRONIC_CARD_INFO_LOADING':
      return {
        ...state,
        loading: { ...state.loading, electronicCardInfo: action.payload },
        error: { ...state.error, electronicCardInfo: null }
      }
    
    case 'SET_ELECTRONIC_CARD_INFO_SUCCESS':
      return {
        ...state,
        electronicCardInfo: action.payload.cardInfo,
        hasElectronicCard: action.payload.hasCard,
        loading: { ...state.loading, electronicCardInfo: false },
        error: { ...state.error, electronicCardInfo: null }
      }
    
    case 'SET_ELECTRONIC_CARD_INFO_ERROR':
      return {
        ...state,
        loading: { ...state.loading, electronicCardInfo: false },
        error: { ...state.error, electronicCardInfo: action.payload }
      }
    
    // 实体卡 Cases
    case 'SET_PHYSICAL_CARD_INFO_LOADING':
      return {
        ...state,
        loading: { ...state.loading, physicalCardInfo: action.payload },
        error: { ...state.error, physicalCardInfo: null }
      }
    
    case 'SET_PHYSICAL_CARD_INFO_SUCCESS':
      return {
        ...state,
        physicalCardInfo: action.payload.cardInfo,
        hasPhysicalCard: action.payload.hasCard,
        loading: { ...state.loading, physicalCardInfo: false },
        error: { ...state.error, physicalCardInfo: null }
      }
    
    case 'SET_PHYSICAL_CARD_INFO_ERROR':
      return {
        ...state,
        loading: { ...state.loading, physicalCardInfo: false },
        error: { ...state.error, physicalCardInfo: action.payload }
      }
    
    // 商家配置 Cases
    case 'SET_MERCHANT_CONFIG_LOADING':
      return {
        ...state,
        loading: { ...state.loading, merchantConfig: action.payload },
        error: { ...state.error, merchantConfig: null }
      }
    
    case 'SET_MERCHANT_CONFIG_SUCCESS':
      return {
        ...state,
        merchantConfig: action.payload,
        loading: { ...state.loading, merchantConfig: false },
        error: { ...state.error, merchantConfig: null }
      }
    
    case 'SET_MERCHANT_CONFIG_ERROR':
      return {
        ...state,
        loading: { ...state.loading, merchantConfig: false },
        error: { ...state.error, merchantConfig: action.payload }
      }
    
    case 'SET_SELECTED_CURRENCY':
      return {
        ...state,
        currencySelection: {
          ...state.currencySelection,
          selectedCurrency: action.payload
        }
      }
    
    case 'SET_CURRENT_NETWORK':
      return {
        ...state,
        currencySelection: {
          ...state.currencySelection,
          currentNetwork: action.payload
        }
      }
    
    case 'CLEAR_CURRENCY_SELECTION':
      return {
        ...state,
        currencySelection: {
          selectedCurrency: null,
          currentNetwork: null
        }
      }
    
    case 'CLEAR_USER_INFO':
      return {
        ...state,
        userInfo: null,
        error: { ...state.error, userInfo: null }
      }
    
    case 'CLEAR_ELECTRONIC_CARD_INFO':
      return {
        ...state,
        electronicCardInfo: null,
        hasElectronicCard: null,
        error: { ...state.error, electronicCardInfo: null }
      }
    
    case 'CLEAR_PHYSICAL_CARD_INFO':
      return {
        ...state,
        physicalCardInfo: null,
        hasPhysicalCard: null,
        error: { ...state.error, physicalCardInfo: null }
      }
    
    case 'CLEAR_MERCHANT_CONFIG':
      return {
        ...state,
        merchantConfig: null,
        error: { ...state.error, merchantConfig: null }
      }
    
    case 'CLEAR_ALL':
      return {
        ...initialState
      }
    
    case 'HYDRATE_FROM_STORAGE':
      return {
        ...state,
        ...action.payload
      }
    
    default:
      return state
  }
}

// Context 类型定义
interface AppContextType {
  state: AppState
  actions: {
    fetchUserInfo: (force?: boolean) => Promise<void>
    fetchElectronicCardInfo: (force?: boolean) => Promise<void>
    fetchPhysicalCardInfo: (force?: boolean) => Promise<void>
    fetchMerchantConfig: (force?: boolean) => Promise<void>
    refreshUserInfo: () => Promise<void>
    refreshElectronicCardInfo: () => Promise<void>
    refreshPhysicalCardInfo: () => Promise<void>
    refreshMerchantConfig: () => Promise<void>
    refreshAll: () => Promise<void>
    clearUserInfo: () => void
    clearElectronicCardInfo: () => void
    clearPhysicalCardInfo: () => void
    clearMerchantConfig: () => void
    clearAll: () => void
    setSelectedCurrency: (currency: CurrencyItem) => void
    setCurrentNetwork: (network: NetworkItem) => void
    clearCurrencySelection: () => void
  }
}

// 创建 Context
const AppStateContext = createContext<AppContextType | undefined>(undefined)

// 持久化相关常量
const STORAGE_KEY = 'app_state'

// 持久化工具函数
const saveToStorage = (state: AppState) => {
  try {
    const dataToSave = {
      userInfo: state.userInfo,
      electronicCardInfo: state.electronicCardInfo,
      hasElectronicCard: state.hasElectronicCard,
      physicalCardInfo: state.physicalCardInfo,
      hasPhysicalCard: state.hasPhysicalCard,
      merchantConfig: state.merchantConfig,
      currencySelection: state.currencySelection
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave))
  } catch (error) {
    console.error('保存状态到本地存储失败:', error)
  }
}

const loadFromStorage = (): Partial<AppState> | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return null
    
    const parsed = JSON.parse(stored)
    
    return {
      userInfo: parsed.userInfo,
      electronicCardInfo: parsed.electronicCardInfo,
      hasElectronicCard: parsed.hasElectronicCard,
      physicalCardInfo: parsed.physicalCardInfo,
      hasPhysicalCard: parsed.hasPhysicalCard,
      merchantConfig: parsed.merchantConfig,
      currencySelection: parsed.currencySelection || {
        selectedCurrency: null,
        currentNetwork: null
      }
    }
  } catch (error) {
    console.error('从本地存储加载状态失败:', error)
    return null
  }
}

// Provider 组件
export const AppStateProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState)

  // 从本地存储恢复状态
  useEffect(() => {
    const storedState = loadFromStorage()
    if (storedState) {
      dispatch({ type: 'HYDRATE_FROM_STORAGE', payload: storedState })
    }
  }, [])

  // 状态变化时保存到本地存储
  useEffect(() => {
    // 关键：使用 hasCard !== null 判断，确保即使 cardInfo 为 null 也会保存
    if (
      state.userInfo || 
      state.hasElectronicCard !== null || 
      state.hasPhysicalCard !== null || 
      state.merchantConfig ||
      state.currencySelection.selectedCurrency
    ) {
      saveToStorage(state)
    }
  }, [state.userInfo, state.electronicCardInfo, state.hasElectronicCard, state.physicalCardInfo, state.hasPhysicalCard, state.merchantConfig, state.currencySelection])

  // 获取用户信息
  const fetchUserInfo = async (force = false) => {
    try {
      dispatch({ type: 'SET_USER_INFO_LOADING', payload: true })
      const userInfo = await APIWeb.getUserInfo()
      dispatch({ type: 'SET_USER_INFO_SUCCESS', payload: userInfo })
    } catch (error: any) {
      dispatch({ type: 'SET_USER_INFO_ERROR', payload: error.message || '获取用户信息失败' })
      console.error('获取用户信息失败:', error)
    }
  }

  // 获取虚拟卡信息
  const fetchElectronicCardInfo = async (force = false) => {
    try {
      dispatch({ type: 'SET_ELECTRONIC_CARD_INFO_LOADING', payload: true })
      const data = await APIWeb.getElectronicCardInfo()
      
      if (data?.code === 1004) {
        // 没有卡片
        dispatch({ 
          type: 'SET_ELECTRONIC_CARD_INFO_SUCCESS', 
          payload: { cardInfo: null, hasCard: false } 
        })
      } else {
        dispatch({ 
          type: 'SET_ELECTRONIC_CARD_INFO_SUCCESS', 
          payload: { cardInfo: data, hasCard: true } 
        })
      }
    } catch (error: any) {
      dispatch({ type: 'SET_ELECTRONIC_CARD_INFO_ERROR', payload: error.message || '获取虚拟卡信息失败' })
      console.error('获取虚拟卡信息失败:', error)
    }
  }

  // 获取实体卡信息
  const fetchPhysicalCardInfo = async (force = false) => {
    try {
      dispatch({ type: 'SET_PHYSICAL_CARD_INFO_LOADING', payload: true })
      const data = await APIWeb.getPhysicalCardInfo()
      
      // 关键：响应拦截器已经处理过，code === 0 时返回 data.data
      // 所以这里直接判断 data.card_no
      if(data?.code === 1004) {
        // 没有卡片
        dispatch({ 
          type: 'SET_PHYSICAL_CARD_INFO_SUCCESS', 
          payload: { cardInfo: null, hasCard: false } 
        })
      } else {
        dispatch({ 
          type: 'SET_PHYSICAL_CARD_INFO_SUCCESS', 
          payload: { cardInfo: data, hasCard: true } 
        })
      }
    } catch (error: any) {
      // 接口报错时也设置为无卡状态
      dispatch({ 
        type: 'SET_PHYSICAL_CARD_INFO_SUCCESS', 
        payload: { cardInfo: null, hasCard: false } 
      })
      console.error('获取实体卡信息失败（可能未申请）:', error)
    }
  }

  // 获取商家配置
  const fetchMerchantConfig = async (force = false) => {
    try {
      dispatch({ type: 'SET_MERCHANT_CONFIG_LOADING', payload: true })
      const config = await APIWeb.getMerchantConfig()
      dispatch({ type: 'SET_MERCHANT_CONFIG_SUCCESS', payload: config })
    } catch (error: any) {
      dispatch({ type: 'SET_MERCHANT_CONFIG_ERROR', payload: error.message || '获取商家配置失败' })
      console.error('获取商家配置失败:', error)
    }
  }

  // 强制刷新用户信息
  const refreshUserInfo = async () => {
    await fetchUserInfo(true)
  }

  // 强制刷新虚拟卡信息
  const refreshElectronicCardInfo = async () => {
    await fetchElectronicCardInfo(true)
  }

  // 强制刷新实体卡信息
  const refreshPhysicalCardInfo = async () => {
    await fetchPhysicalCardInfo(true)
  }

  // 强制刷新商家配置
  const refreshMerchantConfig = async () => {
    await fetchMerchantConfig(true)
  }

  // 刷新所有信息
  const refreshAll = async () => {
    await Promise.all([
      fetchUserInfo(true),
      fetchElectronicCardInfo(true),
      fetchPhysicalCardInfo(true),
      fetchMerchantConfig(true)
    ])
  }

  // 清除用户信息
  const clearUserInfo = () => {
    dispatch({ type: 'CLEAR_USER_INFO' })
  }

  // 清除虚拟卡信息
  const clearElectronicCardInfo = () => {
    dispatch({ type: 'CLEAR_ELECTRONIC_CARD_INFO' })
  }

  // 清除实体卡信息
  const clearPhysicalCardInfo = () => {
    dispatch({ type: 'CLEAR_PHYSICAL_CARD_INFO' })
  }

  // 清除商家配置
  const clearMerchantConfig = () => {
    dispatch({ type: 'CLEAR_MERCHANT_CONFIG' })
  }

  // 清除所有信息
  const clearAll = () => {
    dispatch({ type: 'CLEAR_ALL' })
    localStorage.removeItem(STORAGE_KEY)
  }

  // 设置选中的币种
  const setSelectedCurrency = (currency: CurrencyItem) => {
    dispatch({ type: 'SET_SELECTED_CURRENCY', payload: currency })
  }

  // 设置当前网络
  const setCurrentNetwork = (network: NetworkItem) => {
    dispatch({ type: 'SET_CURRENT_NETWORK', payload: network })
  }

  // 清除币种选择
  const clearCurrencySelection = () => {
    dispatch({ type: 'CLEAR_CURRENCY_SELECTION' })
  }

  const contextValue: AppContextType = {
    state,
    actions: {
      fetchUserInfo,
      fetchElectronicCardInfo,
      fetchPhysicalCardInfo,
      fetchMerchantConfig,
      refreshUserInfo,
      refreshElectronicCardInfo,
      refreshPhysicalCardInfo,
      refreshMerchantConfig,
      refreshAll,
      clearUserInfo,
      clearElectronicCardInfo,
      clearPhysicalCardInfo,
      clearMerchantConfig,
      clearAll,
      setSelectedCurrency,
      setCurrentNetwork,
      clearCurrencySelection
    }
  }

  return (
    <AppStateContext.Provider value={contextValue}>
      {children}
    </AppStateContext.Provider>
  )
}

// Hook 用于使用 Context
export const useAppState = () => {
  const context = useContext(AppStateContext)
  if (context === undefined) {
    throw new Error('useAppState must be used within an AppStateProvider')
  }
  return context
}

// 导出便捷的 hooks
export const useUserInfo = () => {
  const { state, actions } = useAppState()
  
  // 自动获取用户信息（如果没有缓存）
  useEffect(() => {
    if (!state.userInfo && !state.loading.userInfo) {
      actions.fetchUserInfo()
    }
  }, [state.userInfo, state.loading.userInfo, actions])

  return {
    userInfo: state.userInfo,
    loading: state.loading.userInfo,
    error: state.error.userInfo,
    actions: {
      refreshUserInfo: actions.refreshUserInfo,
      clearUserInfo: actions.clearUserInfo,
      fetchUserInfo: actions.fetchUserInfo
    }
  }
}

// 虚拟卡 Hook
export const useElectronicCardInfo = () => {
  const { state, actions } = useAppState()
  
  // 自动获取虚拟卡信息（如果没有缓存）
  useEffect(() => {
    if (state.electronicCardInfo === null && state.hasElectronicCard === null && !state.loading.electronicCardInfo) {
      actions.fetchElectronicCardInfo()
    }
  }, [state.electronicCardInfo, state.hasElectronicCard, state.loading.electronicCardInfo, actions])

  return {
    cardInfo: state.electronicCardInfo,
    hasCard: state.hasElectronicCard,
    loading: state.loading.electronicCardInfo,
    error: state.error.electronicCardInfo,
    actions: {
      refreshCardInfo: actions.refreshElectronicCardInfo,
      clearCardInfo: actions.clearElectronicCardInfo,
      fetchCardInfo: actions.fetchElectronicCardInfo
    }
  }
}

// 实体卡 Hook
export const usePhysicalCardInfo = () => {
  const { state, actions } = useAppState()
  
  // 自动获取实体卡信息（如果没有缓存）
  useEffect(() => {
    if (state.physicalCardInfo === null && state.hasPhysicalCard === null && !state.loading.physicalCardInfo) {
      actions.fetchPhysicalCardInfo()
    }
  }, [state.physicalCardInfo, state.hasPhysicalCard, state.loading.physicalCardInfo, actions])

  return {
    cardInfo: state.physicalCardInfo,
    hasCard: state.hasPhysicalCard,
    loading: state.loading.physicalCardInfo,
    error: state.error.physicalCardInfo,
    actions: {
      refreshCardInfo: actions.refreshPhysicalCardInfo,
      clearCardInfo: actions.clearPhysicalCardInfo,
      fetchCardInfo: actions.fetchPhysicalCardInfo
    }
  }
}

// 商家配置 Hook
export const useMerchantConfig = () => {
  const { state, actions } = useAppState()
  
  // 自动获取商家配置（如果没有缓存）
  useEffect(() => {
    if (!state.merchantConfig && !state.loading.merchantConfig) {
      actions.fetchMerchantConfig()
    }
  }, [state.merchantConfig, state.loading.merchantConfig, actions])

  return {
    merchantConfig: state.merchantConfig,
    loading: state.loading.merchantConfig,
    error: state.error.merchantConfig,
    actions: {
      refreshMerchantConfig: actions.refreshMerchantConfig,
      clearMerchantConfig: actions.clearMerchantConfig,
      fetchMerchantConfig: actions.fetchMerchantConfig
    }
  }
}

// 币种选择相关的便捷hook
export const useCurrencySelection = () => {
  const { state, actions } = useAppState()

  return {
    selectedCurrency: state.currencySelection.selectedCurrency,
    currentNetwork: state.currencySelection.currentNetwork,
    actions: {
      setSelectedCurrency: actions.setSelectedCurrency,
      setCurrentNetwork: actions.setCurrentNetwork,
      clearCurrencySelection: actions.clearCurrencySelection
    }
  }
}

// 导出类型定义供其他组件使用
export type { CurrencyItem, NetworkItem, CurrencySelection }